/** Existing-object selection reuses RecipientInput's combobox, cancellation and keyboard engine. */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RecipientInput, type RecipientDirectory, type RecipientInputLabels } from '../mail/recipient-input';
import type { PersonEntry } from '../mail/recipients';

/** A name equal to its identity (a domain, an address) is shown once, not as "x <x>". */
const displayName = (entry: { label: string; identity: string }): string | null => (entry.label === entry.identity ? null : entry.label);

export interface EntityOption<T extends string | number = number> {
  id: T;
  label: string;
  identity: string;
}

export interface EntityDirectory<T extends string | number = number> {
  search(query: string, signal: AbortSignal, offset: number): Promise<{
    items: readonly EntityOption<T>[];
    nextOffset: number | null;
    capped?: boolean;
  }>;
}

export interface EntityPickerProps<T extends string | number = number> {
  id?: string;
  label: string;
  value: EntityOption<T> | null;
  onChange: (value: EntityOption<T> | null) => void;
  directory: EntityDirectory<T>;
  labels: RecipientInputLabels;
  disabled?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
  placeholder?: string;
  minQueryLength?: number;
  debounceMs?: number;
  autoFocus?: boolean;
}

export interface EntityMultiPickerProps<T extends string | number = number> extends Omit<EntityPickerProps<T>, 'value' | 'onChange'> {
  value: readonly EntityOption<T>[];
  onChange: (value: EntityOption<T>[]) => void;
  maxSelected?: number;
}

/** Existing-object chips retain immutable keys; free text never becomes a selected object. */
export function EntityMultiPicker<T extends string | number = number>({ value, onChange, directory, disabled, maxSelected = 100, ...props }: EntityMultiPickerProps<T>) {
  const previousDirectory = useRef(directory);
  useLayoutEffect(() => {
    if (previousDirectory.current !== directory) { previousDirectory.current = directory; onChange([]); }
  }, [directory, onChange]);
  const adapter = useMemo(() => {
    const entries = new Map<string, EntityOption<T>>();
    const recipients: RecipientDirectory = {
      suggest: async (query, signal) => (await directory.search(query, signal, 0)).items.map(entry => ({ key: String(entry.id), name: displayName(entry), email: entry.identity, source: 'gal' as const, kind: 'person' as const })),
      suggestPage: async (query, signal, offset) => {
        const page = await directory.search(query, signal, offset);
        if (signal.aborted) return { items: [], nextOffset: null };
        for (const entry of page.items) entries.set(String(entry.id), entry);
        return { ...page, items: page.items.map(entry => ({ key: String(entry.id), name: displayName(entry), email: entry.identity, source: 'gal' as const, kind: 'person' as const })) };
      },
      resolve: () => Promise.resolve([]),
    };
    return { entries, recipients };
  }, [directory]);
  return <RecipientInput {...props} field={props.id ?? 'entities'} selectionOnly maxRecipients={maxSelected} readOnly={disabled}
    directory={adapter.recipients}
    value={value.map(entry => ({ id: `entity-${entry.id}`, key: String(entry.id), email: entry.identity, ...(displayName(entry) ? { name: displayName(entry)! } : {}), source: 'gal', kind: 'person', status: 'resolved' }))}
    onChange={next => onChange(next.flatMap(recipient => {
      const entry = recipient.key === undefined ? undefined : adapter.entries.get(recipient.key) ?? value.find(entry => String(entry.id) === recipient.key);
      return entry ? [entry] : [];
    }))} />;
}

export function EntityPicker<T extends string | number = number>({ value, onChange, directory, disabled, ...props }: EntityPickerProps<T>) {
  const previousDirectory = useRef(directory);
  useLayoutEffect(() => {
    if (previousDirectory.current !== directory) {
      previousDirectory.current = directory;
      onChange(null);
    }
  }, [directory, onChange]);
  const adapter = useMemo(() => {
    const entries = new Map<string, EntityOption<T>>();
    const person = (entry: EntityOption<T>): PersonEntry => ({ key: String(entry.id), name: displayName(entry), email: entry.identity, source: 'gal', kind: 'person' });
    const recipients: RecipientDirectory = {
      suggest: async (query, signal) => (await directory.search(query, signal, 0)).items.map(person),
      suggestPage: async (query, signal, offset) => {
        const page = await directory.search(query, signal, offset);
        if (signal.aborted) return { items: [], nextOffset: null };
        for (const entry of page.items) entries.set(String(entry.id), entry);
        return { ...page, items: page.items.map(person) };
      },
      resolve: () => Promise.resolve([]),
    };
    return { entries, recipients };
  }, [directory]);
  return <RecipientInput
    {...props}
    field={props.id ?? 'entity'}
    selectionOnly
    maxRecipients={1}
    readOnly={disabled}
    directory={adapter.recipients}
    value={value ? [{ id: `entity-${value.id}`, key: String(value.id), email: value.identity, ...(displayName(value) ? { name: displayName(value)! } : {}), source: 'gal', kind: 'person', status: 'resolved' }] : []}
    onChange={(next) => {
      const key = next[0]?.key;
      onChange(key === undefined ? null : adapter.entries.get(key) ?? null);
    }}
  />;
}

const LIST_PAGE = 50;

/**
 * A directory over an already authorized, bounded list (domains, organizations, folders, the mailboxes
 * a delegation covers): client-side filtering on label and identity, 50 per page. Large or server-scoped
 * sets (mailboxes, groups) use a server-side directory instead; the component stays the same.
 */
export function listEntityDirectory<T extends string | number = number>(items: () => readonly EntityOption<T>[]): EntityDirectory<T> {
  return {
    search: (query, signal, offset) => {
      if (signal.aborted) return Promise.resolve({ items: [], nextOffset: null });
      const needle = query.trim().toLowerCase();
      const matches = items().filter((entry) => !needle || entry.label.toLowerCase().includes(needle) || entry.identity.toLowerCase().includes(needle));
      const page = matches.slice(offset, offset + LIST_PAGE);
      return Promise.resolve({ items: page, nextOffset: offset + LIST_PAGE < matches.length ? offset + LIST_PAGE : null });
    },
  };
}

export interface EntityListPickerProps<T extends string | number = string> extends Omit<EntityPickerProps<T>, 'value' | 'onChange' | 'directory' | 'minQueryLength'> {
  /** The authorized choices; an empty or still loading list shows the picker without suggestions. */
  items: readonly EntityOption<T>[];
  /** Id of the selected entity; empty/null = none. */
  value: T | null | undefined;
  /** Called with the chosen entity. */
  onChange: (value: EntityOption<T> | null) => void;
  /** Also report null when typing a new query clears the selection; required choices leave this off and keep their value. */
  clearable?: boolean;
}

/** Existing-entity selection from a bounded list: the shared EntityPicker (type-ahead, keyboard, states) over a list directory. */
export function EntityListPicker<T extends string | number = string>(props: EntityListPickerProps<T>) {
  // A changed list remounts the picker, so its first lookup always runs against the current choices.
  const key = props.items.map((entry) => `${entry.id}\u0000${entry.label}`).join('\u0001');
  return <ListPicker<T> key={key} {...props} />;
}

function ListPicker<T extends string | number>({ items, value, onChange, clearable = false, ...props }: EntityListPickerProps<T>) {
  const [directory] = useState(() => listEntityDirectory<T>(() => items));
  const selected = value === null || value === undefined || value === '' ? null : items.find((entry) => entry.id === value) ?? null;
  return <EntityPicker<T> {...props} directory={directory} value={selected} minQueryLength={0} onChange={(entry) => { if (entry || clearable) onChange(entry); }} />;
}
